import { useState, useEffect } from 'react';
import { Ad, AdPerformance, Payment, AdRenewalInfo } from '@/types/ads';
import { useToast } from '@/hooks/use-toast';
import { differenceInHours, parseISO } from 'date-fns';

export const useAdsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeAds, setActiveAds] = useState<Ad[]>([]);
  const [expiredAds, setExpiredAds] = useState<Ad[]>([]);
  const [expiringAds, setExpiringAds] = useState<Ad[]>([]);
  const [renewalModalOpen, setRenewalModalOpen] = useState(false);
  const [currentRenewalInfo, setCurrentRenewalInfo] = useState<AdRenewalInfo | null>(null);
  const { toast } = useToast();
  const [adPerformance, setAdPerformance] = useState<AdPerformance>({
    totalViews: 0,
    viewsChange: 0,
    totalLikes: 0,
    likesChange: 0,
    totalInquiries: 0,
    inquiriesChange: 0,
    allTimeViews: 0,
    allTimeViewsChange: 0,
    allTimeLikes: 0,
    allTimeLikesChange: 0,
    allTimeInquiries: 0,
    allTimeInquiriesChange: 0,
    byType: [],
    lastUpdated: '',
    visibilityRank: {
      homepage: undefined,
      story: undefined,
      gallery: undefined
    }
  });
  const [paymentHistory, setPaymentHistory] = useState<Payment[]>([]);

  useEffect(() => {
    const fetchAdData = () => {
      try {
        const mockActiveAds: Ad[] = [
          {
            id: '1',
            type: 'story',
            status: 'active',
            expiresIn: 5,
            expireDate: '2025-04-16',
            isSponsoredByClinic: true,
            views: 1245,
            likes: 87,
            inquiries: 12,
            doctorId: 1,
            visibilityRank: 2,
            clicks: 134,
            conversionRate: 0.96
          },
          {
            id: '2',
            type: 'featuredDoctor',
            status: 'active',
            expiresIn: 12,
            expireDate: '2025-04-23',
            isSponsoredByClinic: false,
            views: 3456,
            inquiries: 23,
            doctorId: 2,
            visibilityRank: 1,
            clicks: 287,
            conversionRate: 0.67
          },
          {
            id: '3',
            type: 'beforeAfter',
            status: 'paused',
            expiresIn: 3,
            expireDate: '2025-04-14',
            isSponsoredByClinic: true,
            views: 876,
            likes: 42,
            inquiries: 8,
            doctorId: 3,
            clicks: 98,
            conversionRate: 0.91
          },
          {
            id: '4',
            type: 'featuredDoctor',
            status: 'active',
            expiresIn: 20,
            expireDate: '2025-05-02',
            isSponsoredByClinic: false,
            views: 2120,
            inquiries: 18,
            doctorId: 4,
            visibilityRank: 3,
            clicks: 187,
            conversionRate: 0.85
          },
          {
            id: '5',
            type: 'featuredDoctor',
            status: 'active',
            expiresIn: 1,
            expireDate: '2025-04-13',
            isSponsoredByClinic: false,
            views: 1850,
            inquiries: 15,
            doctorId: 5,
            visibilityRank: 2,
            clicks: 165,
            conversionRate: 0.81
          },
          {
            id: '6',
            type: 'story',
            status: 'active',
            expiresIn: 0.04,
            expireDate: '2025-04-12T16:00:00',
            isSponsoredByClinic: true,
            views: 950,
            likes: 62,
            inquiries: 7,
            doctorId: 6,
            clicks: 88,
            conversionRate: 0.74
          },
          {
            id: '7',
            type: 'beforeAfter',
            status: 'active',
            expiresIn: 3,
            expireDate: '2025-04-15',
            isSponsoredByClinic: false,
            views: 1675,
            likes: 94,
            inquiries: 14,
            doctorId: 7,
            clicks: 129,
            conversionRate: 0.84
          }
        ];

        const mockExpiredAds: Ad[] = [
          {
            id: '8',
            type: 'featuredDoctor',
            status: 'expired',
            expiresIn: 0,
            expireDate: '2025-04-10',
            isSponsoredByClinic: false,
            views: 2350,
            inquiries: 21,
            duration: 14,
            startDate: '2025-03-27',
            doctorId: 8,
            clicks: 215,
            conversionRate: 0.89,
            originalPrice: 299
          },
          {
            id: '9',
            type: 'story',
            status: 'expired',
            expiresIn: 0,
            expireDate: '2025-04-11',
            isSponsoredByClinic: true,
            views: 1420,
            likes: 76,
            inquiries: 15,
            duration: 7,
            startDate: '2025-04-04',
            doctorId: 9,
            clicks: 132,
            conversionRate: 1.06,
            originalPrice: 99
          }
        ];

        const allAds = [...mockActiveAds, ...mockExpiredAds];

        const now = new Date();
        const expiring = allAds.filter(ad => {
          if (ad.status !== 'active') return false;
          
          if (!ad.expireDate) return false;
          
          const expireDate = typeof ad.expireDate === 'string' 
            ? (ad.expireDate.includes('T') ? parseISO(ad.expireDate) : new Date(ad.expireDate)) 
            : new Date(ad.expireDate);
            
          const hoursRemaining = differenceInHours(expireDate, now);
          
          return hoursRemaining <= 72 && hoursRemaining > 0;
        });

        const active = allAds.filter(ad => ad.status === 'active');
        const expired = allAds.filter(ad => ad.status === 'expired');

        setActiveAds(active);
        setExpiredAds(expired);
        setExpiringAds(expiring);
        setAdPerformance({
          totalViews: 5577,
          viewsChange: 12,
          totalLikes: 129,
          likesChange: 8,
          totalInquiries: 43,
          inquiriesChange: 15,
          allTimeViews: 24689,
          allTimeViewsChange: 22,
          allTimeLikes: 723,
          allTimeLikesChange: 14,
          allTimeInquiries: 187,
          allTimeInquiriesChange: 18,
          byType: [
            {
              type: 'story',
              views: 1245,
              likes: 87,
              inquiries: 12,
              conversionRate: 0.96
            },
            {
              type: 'featuredDoctor',
              views: 5576,
              inquiries: 41,
              conversionRate: 0.74
            },
            {
              type: 'beforeAfter',
              views: 876,
              likes: 42,
              inquiries: 8,
              conversionRate: 0.91
            }
          ],
          lastUpdated: '2025-04-11',
          visibilityRank: {
            homepage: 1,
            story: 2,
            gallery: undefined
          }
        });
        setPaymentHistory([
          {
            id: '1',
            date: '2025-04-01',
            type: 'story',
            duration: 14,
            amount: 198,
            status: 'paid'
          },
          {
            id: '2',
            date: '2025-03-15',
            type: 'featuredDoctor',
            duration: 30,
            amount: 599,
            status: 'paid'
          },
          {
            id: '3',
            date: '2025-02-28',
            type: 'beforeAfter',
            duration: 7,
            amount: 129,
            status: 'refunded'
          },
          {
            id: '4',
            date: '2025-02-10',
            type: 'story',
            duration: 14,
            amount: 198,
            status: 'paid'
          }
        ]);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch ad data');
        setLoading(false);
      }
    };

    fetchAdData();
  }, []);

  const formatExpirationTime = (ad: Ad) => {
    if (!ad.expireDate) return 'Unknown';
    
    const expireDate = typeof ad.expireDate === 'string' 
      ? (ad.expireDate.includes('T') ? parseISO(ad.expireDate) : new Date(ad.expireDate)) 
      : new Date(ad.expireDate);
      
    const hoursRemaining = differenceInHours(expireDate, new Date());
    
    if (hoursRemaining < 1) return 'Less than 1 hour';
    if (hoursRemaining < 24) return `${Math.floor(hoursRemaining)} hours`;
    if (hoursRemaining < 48) return '1 day';
    return `${Math.floor(hoursRemaining / 24)} days`;
  };

  const getExpirationNotifications = () => {
    return expiringAds.map(ad => {
      const expiresIn = formatExpirationTime(ad);
      const adTypeDisplay = ad.type === 'featuredDoctor' 
        ? 'Featured Doctor Ad' 
        : ad.type === 'beforeAfter' 
          ? 'Before & After Gallery Ad' 
          : `${ad.type.charAt(0).toUpperCase() + ad.type.slice(1)} Ad`;
      
      return {
        id: parseInt(`9${ad.id}`),
        type: 'ad-expiration' as const,
        title: `Ad Expiring Soon`,
        message: `Your ${adTypeDisplay} will expire in ${expiresIn}. Renew now to maintain visibility.`,
        time: new Date().toISOString(),
        link: '/doctor-area/ads',
        adId: ad.id,
        expiresIn: expiresIn
      };
    });
  };

  const calculateRenewalPrice = (ad: Ad): number => {
    const basePriceMap: Record<string, number> = {
      'story': 14,
      'beforeAfter': 19,
      'homepage': 25,
      'featuredDoctor': 25,
      'topStory': 20,
      'galleryPromotion': 18
    };
    
    const basePrice = basePriceMap[ad.type] || 20;
    const duration = ad.duration || 7;
    const currentPrice = basePrice * duration;
    
    const originalPrice = ad.originalPrice || currentPrice;
    
    return currentPrice;
  };

  const prepareRenewalInfo = (adId: string): AdRenewalInfo | null => {
    const ad = [...activeAds, ...expiredAds].find(a => a.id === adId);
    if (!ad) return null;
    
    const currentPrice = calculateRenewalPrice(ad);
    const originalPrice = ad.originalPrice || currentPrice;
    
    return {
      adId: ad.id,
      adType: ad.type,
      duration: ad.duration || 7,
      originalPrice,
      currentPrice,
      priceHasChanged: currentPrice !== originalPrice,
      performance: {
        views: ad.views,
        clicks: ad.clicks,
        inquiries: ad.inquiries,
        conversionRate: ad.conversionRate
      }
    };
  };

  const openRenewalModal = (adId: string) => {
    const renewalInfo = prepareRenewalInfo(adId);
    if (renewalInfo) {
      setCurrentRenewalInfo(renewalInfo);
      setRenewalModalOpen(true);
    } else {
      toast({
        title: "Error",
        description: "Could not find ad information for renewal",
        variant: "destructive"
      });
    }
  };

  const processRenewal = (adId: string, editMode: boolean) => {
    if (editMode) {
      toast({
        title: "Redirecting to Editor",
        description: "You can customize your ad before renewal."
      });
      console.log(`Editing ad ${adId} before renewal`);
    } else {
      toast({
        title: "Processing Renewal",
        description: "Redirecting to payment page."
      });
      console.log(`Direct renewal of ad ${adId}`);
    }
  };

  const renewAd = (adId: string) => {
    openRenewalModal(adId);
  };

  return {
    activeAds,
    expiredAds,
    expiringAds,
    adPerformance,
    paymentHistory,
    loading,
    error,
    formatExpirationTime,
    getExpirationNotifications,
    renewAd,
    renewalModalOpen,
    currentRenewalInfo,
    setRenewalModalOpen,
    processRenewal
  };
};
